import React, { useState, useCallback } from 'react';

const SECTIONS = [
  { id: 'overview', label: 'Overview' },
  { id: 'transition', label: 'API Transition' },
  { id: 'data-flow', label: 'Data Flow' },
  { id: 'collections', label: 'Collections' },
  { id: 'database-api', label: 'Database API' },
  { id: 'clubs-coaches', label: 'Clubs & Coaches' },
  { id: 'activity-types', label: 'Activity Types' },
  { id: 'scheduling', label: 'Scheduling' },
  { id: 'gotchas', label: 'Gotchas' },
];

function AdminDocs() {
  const [activeSection, setActiveSection] = useState('overview');

  const scrollTo = useCallback((id) => {
    setActiveSection(id);
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }, []);

  return (
    <div className="admin-content">
      <a href="#main-content" className="admin-docs-skip-link">
        Skip to main content
      </a>
      <div className="admin-content-panel" id="main-content" tabIndex={-1}>
        <div className="admin-content-top">
          <div className="admin-content-top-heading">
            <h1 className="admin-content-section-title" style={{ fontSize: '2.25rem' }}>API Docs</h1>
            <p className="admin-content-section-desc">
              Internal documentation for the client-side Firestore API layer. All functions live in database.js, adminData.js, clubs.js, and coaches.js.
            </p>
          </div>
        </div>

        <nav className="admin-program-cms-nav" aria-label="Documentation sections">
          {SECTIONS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`admin-program-cms-nav-btn${activeSection === id ? ' admin-program-cms-nav-btn--active' : ''}`}
              onClick={() => scrollTo(id)}
              aria-current={activeSection === id ? 'section' : undefined}
              aria-label={`Jump to ${label} section`}
            >
              {label}
            </button>
          ))}
        </nav>

        <section id="overview" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="overview-heading">
          <h2 id="overview-heading" className="admin-program-cms-section-title">Overview</h2>
          <p className="text-secondary" style={{ marginBottom: 'var(--space-6)' }}>
            All functions live in <Code>database.js</Code>, <Code>adminData.js</Code>, <Code>clubs.js</Code>, and <Code>coaches.js</Code>.
          </p>
          <InfoCard title="Source Files">
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <li><Code>src/utils/database.js</Code> — Core program, activity, workout, and metrics API</li>
              <li><Code>src/utils/adminData.js</Code> — Admin program list, fetch, save, publish</li>
              <li><Code>src/utils/clubs.js</Code> — Club creation, membership, and team management</li>
              <li><Code>src/utils/coaches.js</Code> — Coach creation and membership management</li>
            </ul>
          </InfoCard>
          <InfoCard title="Documentation">
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <li><Code>docs/API.md</Code> — Full API reference (source of truth)</li>
              <li><Code>docs/API_TRANSITION_GUIDE.md</Code> — Migration guide for agents transitioning to DB-driven APIs</li>
            </ul>
          </InfoCard>

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-6)' }}>Quick imports</h3>
          <CodeBlock language="javascript">{`import {
  setupProgramByPersona,
  getCurrentProgram,
  fetchProgramTemplate,
} from '../utils/database';

import {
  getProgramList,
  getProgramByListId,
  saveProgram,
  publishProgram,
} from '../utils/adminData';`}</CodeBlock>
        </section>

        <section id="transition" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="transition-heading">
          <h2 id="transition-heading" className="admin-program-cms-section-title">API Transition Guide</h2>
          <p className="text-secondary" style={{ marginBottom: 'var(--space-4)' }}>
            All program data is DB-driven. No JSON fallback. See <Code>docs/API_TRANSITION_GUIDE.md</Code> for agent migration.
          </p>
          <InfoCard title="Key Rules">
            <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
              <li>Use <Code>getProgramList()</Code> / <Code>getProgramByListId()</Code> from <Code>adminData.js</Code> — not <Code>data/programs</Code></li>
              <li>Use <Code>setupProgramByPersona(userId, programId)</Code> for onboarding/change-program</li>
              <li>Use <Code>getCurrentProgram(userId)</Code> for user&apos;s active program — resolves macro/meso by today</li>
              <li>Use <Code>fetchProgramTemplate(templateId)</Code> to load from <Code>programs/</Code> or <Code>programTemplates/</Code></li>
              <li>If Firestore returns null — show error state, do not fall back to JSON</li>
            </ul>
          </InfoCard>

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-6)' }}>Before (don&apos;t)</h3>
          <CodeBlock language="javascript">{`// ❌ Don't use JSON fallback
import { getProgramByListId } from '../data/programs';
const program = getProgramByListId(id) || fallbackToJson(id);`}</CodeBlock>

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-4)' }}>After (do)</h3>
          <CodeBlock language="javascript">{`// ✅ Use Firestore only
import { getProgramByListId } from '../utils/adminData';
const program = await getProgramByListId(id);
if (!program) showError('Program not found');`}</CodeBlock>
        </section>

        <section id="data-flow" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="data-flow-heading">
          <h2 id="data-flow-heading" className="admin-program-cms-section-title">Data Flow</h2>

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-4)' }}>Admin Publish → User Setup</h3>
          <FlowDiagram steps={[
            'Admin publishes program → programs/{id} or programTemplates/{id}',
            'syncProgramToPersonasConfig() → config/personas',
            'User onboarding / Change Program → setupProgramByPersona(userId, programId)',
            '  → Resolves programId → templateId (e.g. court_volleyball → volleyball)',
            '  → fetchProgramTemplate(templateId) → reads from Firestore',
            '  → setupProgramForUser(userId, template)',
            '  → Clears future scheduledActivities (status=scheduled, date >= today)',
            '  → Creates users/{userId}/programs/{pid}, macrocycles/{mid}, mesocycles/{msid}',
            '  → Generates scheduledActivities (3 strategy paths)',
            '  → Updates user profile (currentProgramId, currentMacrocycleId, currentMesocycleId, templateVersion)',
          ]} />
          <CodeBlock language="javascript">{`// Onboarding or Change Program flow
const { success } = await setupProgramByPersona(userId, 'court_volleyball');
// Resolves court_volleyball → volleyball, fetches template, sets up user collections`}</CodeBlock>

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-4)' }}>getCurrentProgram (User&apos;s Active Program)</h3>
          <FlowDiagram steps={[
            'getCurrentProgram(userId)',
            '  → Reads users/{userId}/programs, macrocycles, mesocycles',
            '  → findActiveMacrocycle(macros, today) — calendar-month aware',
            '  → findActiveMesocycle(mesos, macro, today)',
            '  → Returns { program, macrocycle, mesocycle, currentWeek }',
          ]} />
          <CodeBlock language="javascript">{`// Dashboard, calendar, any UI showing active program
const current = await getCurrentProgram(userId);
if (current) {
  const { program, macrocycle, mesocycle, currentWeek } = current;
  // Use program.name, macrocycle.name, mesocycle.workout_templates, etc.
}`}</CodeBlock>

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-4)' }}>Activity Completion</h3>
          <FlowDiagram steps={[
            'markActivityComplete(userId, activityId, dateStr, true)',
            '  → scheduledActivities/{aid}.status = "completed"',
            '  → activityLog/{date} completedActivities[] + metrics',
            '  → if appleHealthConnected → healthKitExportQueue/{aid}',
          ]} />

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-4)' }}>Workout Session</h3>
          <FlowDiagram steps={[
            'saveWorkoutSession(userId, sessionData, workoutData)',
            '  → workoutSessions/{sid} + exercises + sets subcollections',
            '  → scheduledActivities/{aid}.status = "completed"',
            '  → activityLog/{date} metrics update',
            '  → if appleHealthConnected → healthKitExportQueue',
          ]} />

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-4)' }}>Program Reset</h3>
          <FlowDiagram steps={[
            'checkProgramUpdateAvailable(userId)',
            '  → compares user.templateVersion vs published version',
            'resetProgramWorkouts(userId)',
            '  → deletes future scheduledActivities',
            '  → fetches latest template',
            '  → regenerates activities (same 3 strategies)',
            '  → updates user.templateVersion',
          ]} />
        </section>

        <section id="collections" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="collections-heading">
          <h2 id="collections-heading" className="admin-program-cms-section-title">Firestore Collections</h2>

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-4)' }}>Top-Level</h3>
          <SchemaTable title="programs/{id} & programTemplates/{id}" fields={[
            ['name', 'string', 'Display name (e.g. "Youth Volleyball")'],
            ['programId', 'string', 'Canonical ID'],
            ['programStartDate', 'string', 'YYYY-MM-DD'],
            ['durationWeeks', 'number', 'Total program length'],
            ['macrocycles', 'array', 'Macrocycle definitions with start_month, end_month, weekly_activity_mix'],
            ['mesocycles', 'array', 'Top-level mesocycle definitions with macrocycle_id, workout_templates'],
            ['generatedSchedule', 'object?', 'Pre-built { "YYYY-MM-DD": [activity...] }'],
            ['templateVersion', 'string', 'Version for update detection'],
          ]} />
          <SchemaTable title="workoutTemplates/{templateId}" fields={[
            ['name', 'string', 'Workout title'],
            ['blocks', 'array', 'Exercise blocks with sets/reps'],
          ]} />
          <SchemaTable title="config/personas (singleton)" fields={[
            ['personas', 'array', '[{ id, name, programs: [{ id, programId, name, status }] }]'],
            ['experienceLevels', 'array', 'Experience level options'],
            ['filteringMatrix', 'object', 'Filtering rules'],
          ]} />
          <SchemaTable title="clubs/{clubId}" fields={[
            ['ownerId', 'string', 'User ID of club owner'],
            ['name', 'string', 'Club display name'],
            ['slug', 'string', 'URL-safe unique slug'],
            ['teams', 'array', '[{ id, ageGroup, label }]'],
          ]} />
          <SchemaTable title="coaches/{coachId}" fields={[
            ['ownerId', 'string', 'User ID of coach'],
            ['name', 'string', 'Coach/team name'],
            ['slug', 'string', 'URL-safe unique slug'],
          ]} />

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-6)' }}>User Subcollections</h3>
          <SchemaTable title="users/{userId}" fields={[
            ['currentProgramId', 'string?', 'Active program ID'],
            ['currentMacrocycleId', 'string?', 'Active macrocycle ID'],
            ['currentMesocycleId', 'string?', 'Active mesocycle ID'],
            ['templateVersion', 'string?', 'Last applied template version (for update detection)'],
            ['personaId', 'string?', 'Selected persona'],
            ['appleHealthConnected', 'boolean', 'HealthKit sync enabled'],
            ['paceSettings', 'object?', '{ runPaceSecondsPerMile, bikingAvgMph, ... }'],
          ]} />
          <SchemaTable title="users/{userId}/programs/{programId}" fields={[
            ['id', 'string', 'Program ID (e.g. volleyball)'],
            ['name', 'string', 'Display name'],
            ['programStartDate', 'string', 'YYYY-MM-DD'],
            ['durationWeeks', 'number', ''],
            ['templateVersion', 'string?', 'Version when enrolled'],
            ['status', 'string', 'active'],
          ]} />
          <SchemaTable title="scheduledActivities/{autoId}" fields={[
            ['scheduledDate', 'string', 'YYYY-MM-DD'],
            ['scheduledTime', 'string?', '"5:00 PM" or null if unassigned'],
            ['activityType', 'string', 'strength_workout | practice | game | endurance | recovery | hiit | drills'],
            ['source', 'string', 'program | user'],
            ['workoutTemplateId', 'string?', 'Links to workoutTemplates'],
            ['name', 'string', 'Display name'],
            ['estimatedDuration', 'number', 'Minutes'],
            ['status', 'string', 'scheduled | completed'],
            ['weekNumber', 'number?', 'Week within program'],
          ]} />
          <SchemaTable title="activityLog/{YYYY-MM-DD}" fields={[
            ['date', 'string', 'YYYY-MM-DD'],
            ['hasCompletedActivity', 'boolean', ''],
            ['completedActivities', 'array', '[{ activityId, activityType, name, duration, ... }]'],
            ['metrics', 'object', '{ workoutDuration, totalDistance, lbsMoved, ... }'],
          ]} />
          <SchemaTable title="macrocycles/{macrocycleId}" fields={[
            ['name', 'string?', 'e.g. "Winter-Spring Club Season"'],
            ['startMonth', 'number?', 'Calendar start month (1-12), set in admin'],
            ['endMonth', 'number?', 'Calendar end month (1-12), set in admin'],
            ['startDate', 'string', 'YYYY-MM-DD (computed from start_month/end_month)'],
            ['endDate', 'string', 'YYYY-MM-DD (computed from start_month/end_month)'],
            ['phase', 'string?', 'Training phase'],
            ['order', 'number?', 'Sort order'],
          ]} />
          <SchemaTable title="mesocycles/{mesocycleId}" fields={[
            ['name', 'string?', ''],
            ['startDate', 'string', 'YYYY-MM-DD (computed)'],
            ['endDate', 'string', 'YYYY-MM-DD (computed)'],
            ['totalWeeks', 'number', ''],
            ['strengthPhase', 'string?', 'e.g. hypertrophy, strength'],
            ['macrocycleId', 'string', 'Parent macrocycle'],
          ]} />
          <SchemaTable title="workoutSessions/{sessionId}" fields={[
            ['scheduledActivityId', 'string', 'Links to scheduledActivities'],
            ['workoutTemplateId', 'string?', ''],
            ['name', 'string', ''],
            ['status', 'string', ''],
            ['summary', 'object', '{ totalExercises, exercisesCompleted, totalReps, totalLbsMoved }'],
          ]} />
          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-6)' }}>Example: scheduledActivity document</h3>
          <CodeBlock language="json">{`{
  "scheduledDate": "2026-02-17",
  "scheduledTime": null,
  "activityType": "strength_workout",
  "workoutTemplateId": "vb_inseason_day_a",
  "name": "Lower Body Maintenance",
  "estimatedDuration": 60,
  "status": "scheduled",
  "source": "program"
}`}</CodeBlock>
        </section>

        <section id="database-api" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="database-api-heading">
          <h2 id="database-api-heading" className="admin-program-cms-section-title">Database API</h2>

          <ApiGroup title="User Profile">
            <ApiRow fn="createUserProfile" params="userId, profileData" returns="{success}" desc="Creates/merges user profile" />
            <ApiRow fn="getUserProfile" params="userId" returns="object | null" desc="Fetches user profile" />
            <ApiRow fn="updateUserProfile" params="userId, updates" returns="{success}" desc="Patches fields on user profile" />
          </ApiGroup>

          <ApiGroup title="Program Setup">
            <ApiRow fn="setupProgramForUser" params="userId, programTemplate" returns="{success, activitiesCreated}" desc="Core engine — clears future activities, creates program/macro/meso docs, generates schedule" />
            <ApiRow fn="setupProgramByPersona" params="userId, programId" returns="{success, programType}" desc="Resolves persona → template → calls setupProgramForUser" />
            <ApiRow fn="fetchProgramTemplate" params="templateId" returns="object | null" desc="Fetches from programTemplates/{id}, falls back to programs/{id}" />
            <ApiRow fn="getCurrentProgram" params="userId" returns="{program, macrocycle, mesocycle, currentWeek} | null" desc="Loads user's active program + macro/meso" />
          </ApiGroup>

          <ApiGroup title="Program Update / Reset">
            <ApiRow fn="checkProgramUpdateAvailable" params="userId" returns="{needsUpdate, ...}" desc="Compares user.templateVersion vs published" />
            <ApiRow fn="resetProgramWorkouts" params="userId, templateVersion?" returns="{success, deletedCount, createdCount}" desc="Deletes future activities and regenerates from latest template" />
          </ApiGroup>

          <ApiGroup title="Scheduled Activities">
            <ApiRow fn="getScheduledActivitiesForDate" params="userId, dateStr" returns="Array" desc="Activities for a date, cross-refs activityLog" />
            <ApiRow fn="getScheduledActivitiesForMonth" params="userId, year, month" returns="Array" desc="All activities in a month" />
            <ApiRow fn="getNextStrengthWorkout" params="userId" returns="{workout, date} | null" desc="Next strength workout (30-day lookahead)" />
            <ApiRow fn="addSingleActivity" params="userId, dateStr, activityData" returns="{success, activityId}" desc="Creates a user-added activity" />
            <ApiRow fn="updateActivityDetails" params="userId, activityId, updates" returns="{success}" desc="Updates fields on an activity" />
            <ApiRow fn="deleteActivity" params="userId, activityId" returns="{success}" desc="Deletes a single activity" />
            <ApiRow fn="markActivityComplete" params="userId, activityId, dateStr, isComplete" returns="{success}" desc="Toggles complete, updates activityLog, queues HealthKit" />
            <ApiRow fn="shiftAllActivities" params="userId, days" returns="number" desc="Shifts all activities by N days" />
            <ApiRow fn="clearAllScheduledActivities" params="userId" returns="number" desc="Deletes all scheduled activities" />
          </ApiGroup>
          <CodeBlock language="javascript">{`// Mark activity complete (checkbox tap)
await markActivityComplete(userId, activityId, dateStr, true);

// Get next strength workout
const next = await getNextStrengthWorkout(userId);
if (next) {
  const { workout, date } = next;
}`}</CodeBlock>

          <ApiGroup title="Workout Sessions">
            <ApiRow fn="saveWorkoutSession" params="userId, sessionData, workoutData" returns="{success, sessionId, summary}" desc="Saves session with exercises/sets subcollections" />
            <ApiRow fn="getWorkoutSessionForActivity" params="userId, activityId, dateStr" returns="object | null" desc="Gets session for a scheduled activity" />
            <ApiRow fn="getWorkoutTemplate" params="templateId" returns="object | null" desc="Gets workout template from workoutTemplates/{id}; returns null if missing (no JSON fallback)" />
          </ApiGroup>

          <ApiGroup title="Streaks & Metrics">
            <ApiRow fn="getCompletedActivityDays" params="userId, year, month" returns="number[]" desc="Day-of-month numbers with completed activities" />
            <ApiRow fn="getCurrentStreak" params="userId" returns="number" desc="Consecutive days with activity" />
            <ApiRow fn="getMonthlyStreakData" params="userId, year, month, start?, end?" returns="Array<{value}>" desc="12-point streak chart data" />
            <ApiRow fn="getMonthlyActivityMetrics" params="userId, year, month, start?, end?" returns="{duration[], distance[]}" desc="12-point chart (Apple Health > logs > estimates)" />
            <ApiRow fn="getActivityHeatData" params="userId, days?" returns="Array" desc="Heat-map data for last N days" />
          </ApiGroup>

          <ApiGroup title="HealthKit Queue">
            <ApiRow fn="queueActivityForHealthKitExport" params="userId, activityId, dateStr, data" returns="{success}" desc="Queues for iOS HealthKit sync" />
            <ApiRow fn="getPendingHealthKitExportQueue" params="userId, limit?" returns="Array" desc="Gets pending items" />
            <ApiRow fn="markHealthKitExportSynced" params="userId, activityId, success?, error?" returns="{success}" desc="Marks as synced or failed" />
          </ApiGroup>

          <ApiGroup title="Config / Personas">
            <ApiRow fn="getPersonasConfig" params="" returns="object | null" desc="Reads config/personas" />
            <ApiRow fn="getProgramsCatalog" params="" returns="Object" desc="Map of all program IDs to name/description" />
            <ApiRow fn="syncProgramToPersonasConfig" params="docId, data" returns="void" desc="Updates config/personas when admin publishes" />
            <ApiRow fn="seedPersonasConfig" params="" returns="{success}" desc="Seeds config/personas from local JSON" />
          </ApiGroup>

          <ApiGroup title="Account">
            <ApiRow fn="deleteUserAccount" params="userId" returns="{success}" desc="Deletes all user data + subcollections" />
            <ApiRow fn="backfillActivityLogs" params="userId, year, month" returns="{created, updated}" desc="Creates/repairs activityLog from completed activities" />
          </ApiGroup>

          <h3 className="admin-programs-category-title" style={{ marginTop: 'var(--space-6)' }}>Constants</h3>
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-catalog-exercises-table">
              <thead>
                <tr>
                  <th scope="col">Name</th>
                  <th scope="col">Value</th>
                  <th scope="col">Purpose</th>
                </tr>
              </thead>
              <tbody>
                <tr><td><Code>TEMPLATE_VERSION</Code></td><td><Code>'2026-01-07-v4'</Code></td><td>Current template version for update detection</td></tr>
                <tr><td><Code>UPDATABLE_PROGRAMS</Code></td><td><Code>[...]</Code></td><td>Program IDs that support in-place updates</td></tr>
              </tbody>
            </table>
          </div>
        </section>

        <section id="clubs-coaches" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="clubs-coaches-heading">
          <h2 id="clubs-coaches-heading" className="admin-program-cms-section-title">Clubs & Coaches API</h2>

          <ApiGroup title="Clubs (clubs.js)">
            <ApiRow fn="createClub" params="ownerId, clubData" returns="{clubId, slug, club}" desc="Creates club with unique slug, updates owner profile" />
            <ApiRow fn="getClub" params="clubId" returns="object | null" desc="Fetches club by ID" />
            <ApiRow fn="getClubBySlug" params="slug" returns="object | null" desc="Fetches club by slug" />
            <ApiRow fn="updateClub" params="clubId, updates" returns="object" desc="Updates club document" />
            <ApiRow fn="getClubsByOwner" params="ownerId" returns="Array" desc="Gets all clubs owned by user" />
            <ApiRow fn="createClubMembership" params="userId, clubId, year" returns="membershipId" desc="Creates membership for player" />
            <ApiRow fn="getClubMemberships" params="clubId, status?" returns="Array" desc="Gets memberships for a club" />
            <ApiRow fn="getUserClubMembership" params="userId, clubId" returns="object | null" desc="Gets user's membership for a club" />
            <ApiRow fn="getActiveUserMembership" params="userId" returns="object | null" desc="Gets user's current active membership" />
            <ApiRow fn="deactivateUserMemberships" params="userId" returns="number" desc="Deactivates all active memberships" />
            <ApiRow fn="updateClubTeams" params="clubId, teams" returns="object" desc="Updates teams: [{ id, ageGroup, label }]" />
            <ApiRow fn="getTeamSchedules" params="clubId" returns="Array" desc="Gets all team schedules" />
            <ApiRow fn="getTeamSchedule" params="clubId, teamId" returns="object | null" desc="Gets single team schedule" />
            <ApiRow fn="updateTeamSchedule" params="clubId, teamId, data" returns="object" desc="Creates/updates team schedule" />
          </ApiGroup>

          <ApiGroup title="Coaches (coaches.js)">
            <ApiRow fn="createCoach" params="ownerId, coachData" returns="{coachId, slug, coach}" desc="Creates coach, updates owner profile" />
            <ApiRow fn="getCoach" params="coachId" returns="object | null" desc="Fetches coach by ID" />
            <ApiRow fn="getCoachBySlug" params="slug" returns="object | null" desc="Fetches coach by slug" />
            <ApiRow fn="updateCoach" params="coachId, updates" returns="object" desc="Updates coach document" />
            <ApiRow fn="createCoachMembership" params="userId, coachId" returns="membershipId" desc="Creates membership (uses club_memberships)" />
            <ApiRow fn="getCoachMemberships" params="coachId, status?" returns="Array" desc="Gets memberships for a coach" />
          </ApiGroup>
          <CodeBlock language="javascript">{`// Create club
const { clubId, slug, club } = await createClub(ownerId, { name: 'My Club' });

// Get club by slug (for public URLs)
const club = await getClubBySlug('my-club');`}</CodeBlock>
        </section>

        <section id="activity-types" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="activity-types-heading">
          <h2 id="activity-types-heading" className="admin-program-cms-section-title">Activity Type Mapping</h2>
          <p className="text-secondary" style={{ marginBottom: 'var(--space-4)' }}>
            The admin defines activities using string values in <Code>weekly_activity_mix.day_assignments</Code>.
            The <Code>mapAdminActivityType()</Code> function converts them to internal types used by the app.
          </p>
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-catalog-exercises-table">
              <thead>
                <tr>
                  <th scope="col">Admin Value</th>
                  <th scope="col">Internal activityType</th>
                  <th scope="col">Display Name</th>
                  <th scope="col">Duration</th>
                </tr>
              </thead>
              <tbody>
                {[
                  ['strength', 'strength_workout', 'Strength', '50 min'],
                  ['game', 'game', 'Game', '120 min'],
                  ['practice', 'practice', 'Practice', '120 min'],
                  ['practice_or_game', 'practice', 'Practice / Game', '120 min'],
                  ['drills', 'drills', 'Drills', '60 min'],
                  ['cardio', 'endurance', 'Cardio', '45 min'],
                  ['running', 'endurance', 'Run', '45 min'],
                  ['swimming', 'endurance', 'Swim', '60 min'],
                  ['biking', 'endurance', 'Bike', '60 min'],
                  ['yoga', 'recovery', 'Yoga', '45 min'],
                  ['hiit', 'hiit', 'HIIT', '30 min'],
                  ['walking', 'endurance', 'Walk', '30 min'],
                ].map(([admin, internal, display, dur]) => (
                  <tr key={admin}>
                    <td><Code>{admin}</Code></td>
                    <td><Code>{internal}</Code></td>
                    <td>{display}</td>
                    <td>{dur}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-secondary text-sm" style={{ marginTop: 'var(--space-2)' }}>
            Unknown string types fall back to <Code>{'{ activityType: rawType, name: Capitalized(rawType), duration: 60 }'}</Code>. Null / non-string input returns <Code>{'{ activityType: rest, name: Rest, duration: 0 }'}</Code>.
          </p>
          <CodeBlock language="javascript">{`// mapAdminActivityType maps admin strings → internal types
// 'strength' → 'strength_workout', 'practice_or_game' → 'practice', etc.`}</CodeBlock>
        </section>

        <section id="scheduling" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="scheduling-heading">
          <h2 id="scheduling-heading" className="admin-program-cms-section-title">Scheduling Strategies</h2>
          <p className="text-secondary" style={{ marginBottom: 'var(--space-4)' }}>
            <Code>setupProgramForUser</Code> and <Code>resetProgramWorkouts</Code> use three strategies in priority order:
          </p>

          <div className="admin-program-meso-list">
            <StrategyCard
              num={1}
              title="generatedSchedule"
              priority="Highest"
              description="A pre-built calendar object created by the microcycle generator. Keys are dates, values are activity arrays with progression modifiers."
              format={'{ "2026-02-17": [{ activityType, name, workoutTemplateId, mesocycleId, mesoWeekIndex }] }'}
            />
            <StrategyCard
              num={2}
              title="weekly_activity_mix"
              priority="Default"
              description="Calendar-anchored scheduling. Each macrocycle has start_month/end_month (1-12) tying it to real calendar months (e.g. Aug–Nov). Users join programs already in progress — scheduling starts from TODAY. The loop finds the active macrocycle for each date by calendar month, then reads its day_assignments."
              format={'macrocycles[].start_month = 8, end_month = 11\nmacrocycles[].weekly_activity_mix.day_assignments = { saturday: ["game"], tuesday: ["strength"], ... }'}
            />
            <StrategyCard
              num={3}
              title="weeklyTemplates + seasonalRules"
              priority="Legacy"
              description="Multi-sport triathlon programs. Selects a weekly template based on swimming/biking season windows, then rotates through workout templates by day-of-week."
              format={'weeklyTemplates: { swimOn_bikeOn: {...}, swimOff_bikeOff: {...} }'}
            />
          </div>
          <CodeBlock language="json">{`// generatedSchedule (strategy 1) — pre-built
{
  "2026-08-03": [{
    "activityType": "strength_workout",
    "workoutTemplateId": "vb_inseason_day_a",
    "mesocycleId": "vb_fall_meso1",
    "mesoWeekIndex": 1,
    "intensityModifier": 0.7,
    "volumeModifier": 0.7
  }]
}`}</CodeBlock>
        </section>

        <section id="gotchas" className="admin-program-cms-section" style={{ scrollMarginTop: '140px' }} aria-labelledby="gotchas-heading">
          <h2 id="gotchas-heading" className="admin-program-cms-section-title">Gotchas & Lessons Learned</h2>

          <GotchaCard
            num={1}
            title="Firestore rejects undefined"
            problem="batch.set() throws if any field value is undefined."
            rule="Every field must fall back to null, [], or {}."
            code={'difficulty: programTemplate.difficulty || null'}
          />
          <GotchaCard
            num={2}
            title="Macrocycles are calendar-anchored"
            problem="Users join programs already in progress. Macrocycles represent fixed calendar periods (Aug–Nov, Jan–Jun). Sequential date stacking from a start date puts users in the wrong macrocycle."
            rule="Set start_month/end_month (1-12) on each macrocycle in the admin. The code resolves calendar date ranges via macroMonthRange() and schedules from TODAY."
            code={'// Admin: start_month: 8, end_month: 11\n// Code resolves: startDate: "2025-08-01", endDate: "2025-11-30"'}
          />
          <GotchaCard
            num={3}
            title="Mesocycles live in two places"
            problem="Admin stores mesocycles both nested (macrocycle.mesocycles[]) and top-level (programTemplate.mesocycles[]). Top-level has full data."
            rule="Merge from both sources, preferring top-level data."
          />
          <GotchaCard
            num={4}
            title="ID field names are inconsistent"
            problem="Macrocycle/mesocycle objects use id, macrocycleId, macrocycle_id, mesocycleId, mesocycle_id interchangeably."
            rule="Always check all variants."
            code={"const macroId = m.macrocycleId || m.macrocycle_id || m.id;"}
          />
          <GotchaCard
            num={5}
            title="Null scheduledTime should stay null"
            problem='Defaulting scheduledTime to "00:00" shows "12:00 AM" for unassigned times.'
            rule="Preserve null. Only display time UI when explicitly set."
          />
          <GotchaCard
            num={6}
            title="Activity names should be specific"
            problem={"Generic names like \"Activity\" or \"Strength\" appear when the scheduling code doesn't store workout names."}
            rule="For strength, pre-fetch workoutTemplates/{id}.name. For others, use ACTIVITY_TYPE_MAP display names."
          />
          <GotchaCard
            num={7}
            title="Old activities persist after program change"
            problem="Switching programs without cleanup mixes old and new activities."
            rule='Delete future "scheduled" activities before creating new ones.'
            code={'query(ref, where("scheduledDate", ">=", todayStr), where("status", "==", "scheduled"))'}
          />
          <GotchaCard
            num={8}
            title="Design system adherence"
            problem="Inline styles for buttons/cards break visual consistency."
            rule='Use existing CSS classes: onboarding-back, onboarding-submit, btn btn--primary, etc.'
          />
        </section>
      </div>
    </div>
  );
}

function Code({ children }) {
  return (
    <code
      className="admin-docs-inline-code"
      aria-label={typeof children === 'string' ? `Code: ${children}` : undefined}
    >
      {children}
    </code>
  );
}

function CodeBlock({ language, children }) {
  const [copied, setCopied] = useState(false);
  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(children);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [children]);

  return (
    <div className="admin-docs-code-block" role="region" aria-label={`Code block: ${language}`}>
      <pre>
        <code>{children}</code>
      </pre>
      <button
        type="button"
        className={`admin-docs-code-block-copy${copied ? ' copied' : ''}`}
        onClick={handleCopy}
        aria-label={copied ? 'Copied to clipboard' : 'Copy code'}
      >
        {copied ? 'Copied' : 'Copy'}
      </button>
    </div>
  );
}

function InfoCard({ title, children }) {
  return (
    <div className="admin-tier-edit-area" style={{ marginBottom: 'var(--space-4)' }} role="region" aria-label={title || undefined}>
      {title && <h4 className="admin-tier-edit-area-title" style={{ margin: '0 0 var(--space-2)' }}>{title}</h4>}
      {children}
    </div>
  );
}

function FlowDiagram({ steps }) {
  return (
    <div
      className="admin-tier-edit-area"
      style={{ marginBottom: 'var(--space-4)', fontFamily: 'monospace', fontSize: 'var(--font-size-sm)', lineHeight: 1.7 }}
      role="region"
      aria-label="Data flow steps"
    >
      {steps.map((step, i) => (
        <div key={i} style={step.startsWith('  →') ? { paddingLeft: 'var(--space-5)', color: 'var(--color-text-muted)' } : {}}>
          {step.startsWith('  →') ? step : step}
        </div>
      ))}
    </div>
  );
}

function SchemaTable({ title, fields }) {
  return (
    <div style={{ marginBottom: 'var(--space-6)' }}>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-catalog-exercises-table">
          <caption className="admin-programs-category-title" style={{ marginBottom: 'var(--space-2)', textAlign: 'left' }}>{title}</caption>
          <thead>
            <tr>
              <th scope="col">Field</th>
              <th scope="col">Type</th>
              <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>
            {fields.map(([field, type, desc]) => (
              <tr key={field}>
                <td><Code>{field}</Code></td>
                <td className="text-muted text-sm">{type}</td>
                <td>{desc}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function ApiGroup({ title, children }) {
  return (
    <div style={{ marginBottom: 'var(--space-6)' }}>
      <h4 className="admin-programs-category-title" style={{ marginBottom: 'var(--space-2)' }}>{title}</h4>
      <div style={{ overflowX: 'auto' }}>
        <table className="admin-catalog-exercises-table">
          <thead>
            <tr>
              <th scope="col">Function</th>
              <th scope="col">Parameters</th>
              <th scope="col">Returns</th>
              <th scope="col">Description</th>
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}

function ApiRow({ fn, params, returns, desc }) {
  return (
    <tr>
      <td><Code>{fn}</Code></td>
      <td className="text-muted text-sm">{params}</td>
      <td className="text-muted text-sm">{returns}</td>
      <td>{desc}</td>
    </tr>
  );
}

function StrategyCard({ num, title, priority, description, format }) {
  return (
    <div className="admin-program-meso-item" style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <span style={{ width: 24, height: 24, borderRadius: '50%', background: 'var(--color-primary)', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xs)', fontWeight: 600 }} aria-hidden>{num}</span>
        <span className="font-semibold">{title}</span>
        <span className="text-muted text-xs" style={{ marginLeft: 'auto' }}>{priority}</span>
      </div>
      <p className="text-secondary text-sm" style={{ margin: 0 }}>{description}</p>
      {format && <CodeBlock language="javascript">{format}</CodeBlock>}
    </div>
  );
}

function GotchaCard({ num, title, problem, rule, code }) {
  return (
    <div className="admin-program-meso-item" style={{ flexDirection: 'column', alignItems: 'stretch', cursor: 'default' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-2)' }}>
        <span style={{ width: 24, height: 24, borderRadius: '50%', background: '#f59e0b', color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--font-size-xs)', fontWeight: 600 }} aria-hidden>{num}</span>
        <span className="font-semibold">{title}</span>
      </div>
      <div className="text-secondary text-sm" style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
        <div><span className="text-accent font-medium" style={{ marginRight: 'var(--space-2)' }}>Problem:</span>{problem}</div>
        <div><span className="font-medium" style={{ color: 'var(--color-success)', marginRight: 'var(--space-2)' }}>Rule:</span>{rule}</div>
        {code && <CodeBlock language="javascript">{code}</CodeBlock>}
      </div>
    </div>
  );
}

export default AdminDocs;
